import React from "react";
import { motion } from "framer-motion";
import { MessagesSquare, Trophy, Flame, Users } from "lucide-react";
import { ROUNDTABLE_IMG } from "./constants";

const ITEMS = [
    {
        icon: MessagesSquare,
        title: "Real 1:1 Conversations",
        body: "Facilitated discussions with members who share your values. No small talk, no pitch-deck ambushes.",
    },
    {
        icon: Flame,
        title: "Community Challenges",
        body: "Monthly growth-focused activities designed to stretch your network, your skills and your thinking.",
    },
    {
        icon: Trophy,
        title: "Business Wins & Insights",
        body: "Share what's working and learn from others, in a supportive, non-competitive environment.",
    },
    {
        icon: Users,
        title: "Group Discussions",
        body: "Deeper conversations that go well beyond surface-level networking topics.",
    },
];

const Roundtable = () => {
    return (
        <section
            id="roundtable"
            data-testid="roundtable-section"
            className="relative border-y border-[#d4af37]/15 bg-[#080a1f]"
        >
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-center">
                <div className="relative">
                    <div className="relative overflow-hidden rounded-sm border border-[#d4af37]/20">
                        <img
                            src={ROUNDTABLE_IMG}
                            alt="Weekly Roundtable at Deeper Networking"
                            className="w-full h-full object-cover aspect-[4/5]"
                            data-testid="roundtable-image"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0c27] via-[#0a0c27]/20 to-[#0a0c27]/40" />
                        <div className="absolute bottom-6 left-6 right-6 text-[#f6f1e1]">
                            <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#f2c652] mb-2">
                                Every Week
                            </div>
                            <div className="font-display font-black text-2xl md:text-3xl tracking-tight leading-tight">
                                Our weekly anchor.
                            </div>
                        </div>
                    </div>
                </div>

                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#f2c652] font-display mb-4">
                        IV. The Roundtable
                    </div>
                    <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.95] text-[#f6f1e1] mb-6">
                        Genuine connection,{" "}
                        <span className="font-serif-italic font-light text-[#f2c652]">
                            on the calendar.
                        </span>
                    </h2>
                    <p className="text-[#f6f1e1]/60 font-body text-base md:text-lg leading-relaxed mb-10 max-w-xl">
                        Every week, the Roundtable brings members together for
                        real connection and growth. Show up once and you'll
                        feel the difference immediately.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#d4af37]/15 border border-[#d4af37]/20">
                        {ITEMS.map((it, i) => {
                            const Icon = it.icon;
                            return (
                                <motion.div
                                    key={it.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 0.5,
                                        delay: i * 0.07,
                                    }}
                                    className="bg-[#0e1130] p-6 md:p-7 hover:bg-[#141838] transition-colors group"
                                    data-testid={`roundtable-item-${i + 1}`}
                                >
                                    <Icon
                                        className="w-6 h-6 text-[#d4af37] group-hover:text-[#f2c652] transition-colors mb-5"
                                        strokeWidth={1.5}
                                    />
                                    <h3 className="font-display font-bold text-[#f6f1e1] text-lg mb-2 tracking-tight">
                                        {it.title}
                                    </h3>
                                    <p className="text-[#f6f1e1]/55 font-body text-sm leading-relaxed">
                                        {it.body}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Roundtable;
